import {
  ArrowLeft,
  Calendar,
  CheckCircle,
  Clock,
  Filter,
  MapPin,
  MessageSquare,
  Search,
  Star,
  User
} from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

const CustomerFeedback = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'rated', 'unrated'
  const [searchTerm, setSearchTerm] = useState('');
  const [ratedJobs, setRatedJobs] = useState(new Set());

  useEffect(() => {
    fetchJobsAndRatings();
  }, []);

  const fetchJobsAndRatings = async () => {
    try {
      setLoading(true);
      
      // Fetch all completed jobs
      const response = await api.jobs.getUserJobs({ status: 'COMPLETED' });
      const completedJobs = response.jobs || [];
      
      // Check which jobs already have ratings
      const ratedJobIds = new Set();
      for (const job of completedJobs) {
        try {
          // Silently check for existing rating - suppress all console output
          const originalConsoleError = console.error;
          console.error = () => {}; // Temporarily disable console.error
          
          const ratingResponse = await fetch(`http://localhost:5000/api/jobs/${job._id}/rating`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
          
          console.error = originalConsoleError; // Restore console.error
          
          if (ratingResponse.ok) {
            ratedJobIds.add(job._id);
          }
          // If not ok (404), job is not rated yet - that's fine, no error logging
        } catch (error) {
          // Network or other errors - still don't log for rating checks
        }
      }
      
      setJobs(completedJobs);
      setRatedJobs(ratedJobIds);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job => {
    // Filter by rating status
    const hasRating = ratedJobs.has(job._id);
    if (filter === 'rated' && !hasRating) return false;
    if (filter === 'unrated' && hasRating) return false;
    
    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return job.title?.toLowerCase().includes(searchLower) ||
             job.vendorId?.name?.toLowerCase().includes(searchLower) ||
             job.vendorId?.companyName?.toLowerCase().includes(searchLower);
    }
    
    return true;
  });

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-0" style={{ backgroundColor: '#F9FAFB' }}>
        <div className="bg-gradient-to-r from-orange-500 to-red-500 shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="h-16"></div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-8"></div>
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-0" style={{ backgroundColor: '#F9FAFB' }}>
      {/* Page Header */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link
              to="/dashboard"
              className="flex items-center text-orange-100 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Dashboard
            </Link>
            <div className="flex items-center space-x-3">
              <MessageSquare className="w-6 h-6 text-orange-100" />
              <div>
                <h1 className="text-2xl font-bold text-white">Rate Your Service Experience</h1>
                <p className="text-orange-100">Share feedback about your completed jobs to help improve service quality</p>
              </div>
            </div>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by job title or vendor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            {/* Filter */}
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-orange-600" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-medium"
              >
                <option value="all">All Jobs</option>
                <option value="unrated">Pending Rating</option>
                <option value="rated">Already Rated</option>
              </select>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 mt-6 pt-6 border-t border-gray-200">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">{jobs.length}</div>
              <div className="text-sm text-gray-600 mt-1">Total Jobs</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600">{jobs.length - ratedJobs.size}</div>
              <div className="text-sm text-gray-600 mt-1">Pending Rating</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">{ratedJobs.size}</div>
              <div className="text-sm text-gray-600 mt-1">Rated</div>
            </div>
          </div>
        </div>

        {/* Jobs List */}
        {filteredJobs.length > 0 ? (
          <div className="space-y-6">
            {filteredJobs.map((job) => {
              const hasRating = ratedJobs.has(job._id);

              return (
                <motion.div
                  key={job._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Job Info */}
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-3">
                            {job.title}
                          </h3>
                          <div className="flex items-center text-sm text-gray-600 space-x-6">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1.5 text-orange-600" />
                              {new Date(job.completedAt || job.updatedAt).toLocaleDateString()}
                            </div>
                            <div className="flex items-center">
                              <MapPin className="h-4 w-4 mr-1.5 text-orange-600" />
                              {job.address?.city || 'Location not specified'}
                            </div>
                            <div className="flex items-center">
                              <CheckCircle className="h-4 w-4 mr-1.5 text-green-600" />
                              Completed
                            </div>
                          </div>
                        </div>

                        {/* Rating Status */}
                        <div className="flex items-center">
                          {hasRating ? (
                            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold bg-green-50 text-green-700 border border-green-200">
                              <Star className="h-4 w-4 mr-1.5 fill-current" />
                              Rated
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold bg-orange-100 text-orange-700 border border-orange-200">
                              <Clock className="h-4 w-4 mr-1.5" />
                              Pending Rating
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Vendor Info */}
                      <div className="bg-orange-50 rounded-lg p-4 mb-4 border border-orange-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="bg-orange-500 rounded-full p-2.5 mr-3">
                              <User className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900">
                                {job.vendorId?.name || 'Vendor'}
                              </div>
                              {job.vendorId?.companyName && (
                                <div className="text-sm text-gray-600">
                                  {job.vendorId.companyName}
                                </div>
                              )}
                            </div>
                          </div>

                          {job.vendorId?.currentRating && (
                            <div className="flex items-center">
                              <div className="flex mr-2">
                                {renderStars(Math.round(job.vendorId.currentRating))}
                              </div>
                              <span className="text-sm font-medium text-gray-700">
                                ({job.vendorId.currentRating.toFixed(1)})
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Job Description */}
                      {job.description && (
                        <div className="bg-gray-50 rounded-lg p-3 mb-4">
                          <p className="text-gray-700 text-sm line-clamp-2">
                            Request for {job.title} service
                          </p>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <div className="flex items-center space-x-4">
                          {job.totalAmount && (
                            <span className="text-2xl font-bold text-orange-600">
                              ${job.totalAmount.toFixed(2)}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center space-x-3">
                          <Link
                            to={`/jobs/${job._id}`}
                            className="px-5 py-2.5 text-gray-700 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                          >
                            View Details
                          </Link>

                          {hasRating ? (
                            <Link
                              to={`/rate-vendor/${job._id}`}
                              className="flex items-center px-5 py-2.5 bg-green-50 text-green-700 border-2 border-green-200 rounded-lg hover:bg-green-100 transition-colors font-medium"
                            >
                              <MessageSquare className="h-4 w-4 mr-2" />
                              View Rating
                            </Link>
                          ) : (
                            <Link
                              to={`/rate-vendor/${job._id}`}
                              className="flex items-center px-5 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all shadow-md hover:shadow-lg font-semibold"
                            >
                              <Star className="h-4 w-4 mr-2" />
                              Rate Service
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            {jobs.length === 0 ? (
              <>
                <div className="bg-orange-50 rounded-full p-6 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                  <CheckCircle className="h-10 w-10 text-orange-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">No Completed Jobs</h3>
                <p className="text-gray-600 mb-8 max-w-md mx-auto">
                  Complete some jobs to start rating your service experience
                </p>
                <Link
                  to="/jobs/create"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all shadow-md hover:shadow-lg font-semibold"
                >
                  Book a Service
                </Link>
              </>
            ) : (
              <>
                <div className="bg-gray-50 rounded-full p-6 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                  <Search className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">No Jobs Found</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  Try adjusting your search or filter criteria
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerFeedback;
